import {Injectable, inject, DestroyRef} from '@angular/core';
import {Observable, Subject} from 'rxjs';
import {filter, tap} from 'rxjs/operators';
import {Notification} from 'src/app/models/notification/notification';
import {IMessage} from '@stomp/stompjs';
import {FabricService} from 'src/app/services/fabric/fabric.service';
import {Token} from 'src/app/models/notification/token';
import {Page} from 'src/app/models/page';
import {NotificationFilter} from 'src/app/models/notification/notification-filter';
import {NotificationFilterEnum} from 'src/app/models/notification/notification-filter-enum';
import { HttpClient, HttpHeaders, HttpXsrfTokenExtractor } from '@angular/common/http';
import {ConfigurationService} from './configuration.service';
import {KeyValueMap} from 'src/app/models/key-value-map';
import {flatObject, getByPath} from 'src/app/services/util/utils';
import {isFunction} from '@jsverse/transloco';
import {OperationType} from 'src/app/models/workflow-approval/operation-type';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TemenosRxStompConfig } from '../util/rx-stomp-config';
import { RxStompService } from '../util/rx-stomp-service';

const SERVICE_NAME = 'mgmt-notification-resources';

const NOTIFICATION_STATUS_TO_COLOR: KeyValueMap<string, any> = {};

function statusArrayToColors(statusArr: string[], color: any): void {
  for (const status of statusArr) {
    NOTIFICATION_STATUS_TO_COLOR[status] = color;
  }
}

statusArrayToColors(['CREATED', 'UPDATED', 'DELETED', 'DEPLOYED', 'TERMINATED', 'CONFIGURED', 'APPROVED', 'FINAL'], {
  statusColor: 'green'
});

statusArrayToColors(['DEPLOYING', 'TERMINATING', 'CONFIGURING'], {
  statusColor: 'blue'
});

statusArrayToColors(['FAILED', 'TERMINATION FAILED', 'CONFIGURATION FAILED'], {
  statusColor: 'red',
  messageColor: 'message-color-red'
});

const defaultColors = {
  statusColor: 'blue-neutral'
};

type StringProcessor<T> = (arg: T) => string;

interface NotificationProcessor {
  title: string | StringProcessor<Notification>;
  message: string | StringProcessor<Notification>;
  link: string | StringProcessor<Notification>;
}

const NOTIFICATION_PROCESSORS: KeyValueMap<string, NotificationProcessor> = {
  'create instance created': {
    title: (notification) => `${notification.data?.solutionName} - ${notification.data?.blueprintName}`,
    message: (notification) => `${notification.resourceInfo.resourceName} deployment is completed successfully.`,
    link: 'SERVICE_LINK'
  },
  'update configuration final': {
    title: (notification) => `${notification.data?.solutionName} - ${notification.data?.blueprintName}`,
    message: (notification) => `${notification.resourceInfo.resourceName} has been published.`,
    link: 'SERVICE_LINK'
  },
  'update workflow approval approved': {
    title: (notification) => `${notification.data?.solutionName} - ${notification.data?.blueprintName}`,
    message: (notification) => {
      let operation = '';
      if (notification.data.operationType === OperationType.CREATE_INSTANCE) {
        operation = 'Deployment';
      } else if (notification.data.operationType === OperationType.UPDATE_INSTANCE) {
        operation = 'Update';
      }
      return `${operation} request has been approved.`;
    },
    link: 'SERVICE_LINK'
  },
  'apply configuration applied': {
    title: (notification) => `${notification.data?.solutionName} - ${notification.data?.blueprintName}`,
    message: (notification) => `${notification.data?.instanceName} \
      has been updated successfully with ${notification.resourceInfo.resourceName}.`,
    link: 'SERVICE_LINK'
  }
};

@Injectable({
  providedIn: 'root'
})
export class NotificationsService  {
private destroyRef = inject(DestroyRef);

  unreadNotificationsCount = 0;

  notifications: Notification[] = [];
  notificationSubject: Subject<Notification> = new Subject();

  constructor(private fabricService: FabricService,
              private rxStompService: RxStompService,
              private httpXsrfTokenExtractor: HttpXsrfTokenExtractor,
              private configurationService: ConfigurationService) {
    
    this.subscribeForServerNotifications();
  }

  initialize(): void {
    this.rxStompService.configure(new TemenosRxStompConfig(this.httpXsrfTokenExtractor, this, this.configurationService));
    this.rxStompService.activate();
    // this.loadNotifications();
  }

  getNotifications(): Notification[] {
    return this.notifications;
  }

  markAsRead(id: string): Observable<Notification[]> {
    return this.fabricService.invokeOperation<Notification[]>(SERVICE_NAME, 'put_notifications_mark-as-read', {notificationIds: [id]})
      .pipe(
        tap((notifications) => {
            notifications.forEach((readNotification) => {
              const objIndex = this.notifications.findIndex((notification => notification.id === readNotification.id));
              this.notifications[objIndex] = this.parseNotification(readNotification);
              this.unreadNotificationsCount--;
            });
          }
        )
      );
  }

  getToken(): Observable<Token> {
    return this.fabricService.invokeOperation<Token>(SERVICE_NAME, 'get_notifications_get-token');
  }

  onAnyNotification(notificationFilters?: KeyValueMap<string, any>[]): Observable<Notification> {
    const observable = this.notificationSubject.asObservable();

    if (notificationFilters) {
      const flatFilters = notificationFilters.map(notificationFilter => flatObject(notificationFilter));
      return observable.pipe(filter(notification => this.filterNotification(notification, flatFilters)));
    }

    return observable;
  }

  /**
   * Function to subscribe to certain notifications
   *
   * @param notificationFilter all fields of this object should be equal to the correspondent fields in the notification
   * @returns observable which returns notifications which match the filter
   */
  onNotification(notificationFilter?: KeyValueMap<string, any>): Observable<Notification> {
    return this.onAnyNotification([notificationFilter]);
  }

  private loadNotifications(): void {
    const notificationFilter: NotificationFilter = {} as NotificationFilter;
    notificationFilter.filter = NotificationFilterEnum.ALL;

    this.fabricService.invokeOperation<Page<Notification>>(SERVICE_NAME, 'post_notifications_search', notificationFilter)
      .pipe(
        tap((page) => {
            if (page.content) {
              page.content.forEach((notification) => {
                this.addNotification(notification);
              });
            }
          }
        ),
        takeUntilDestroyed(this.destroyRef),
      ).subscribe();
  }

  private addNotification(notification: Notification): void {
    const index = this.notifications.findIndex(tempNotification => tempNotification.id === notification.id);
    if (index < 0) {
      this.notifications.push(this.parseNotification(notification));
      this.notifications.sort(function (a, b) {
        return new Date(b.eventInfo.eventAt).getTime() - new Date(a.eventInfo.eventAt).getTime();
      });
      if (!notification.state.read) {
        this.unreadNotificationsCount++;
      }
    }
  }

  private filterNotification(notification, flatFilters): boolean {
    return flatFilters.some(flatFilter => Object.keys(flatFilter).every(notificationFilterKey =>
      getByPath(notification, notificationFilterKey) === flatFilter[notificationFilterKey]
    ));
  }

  private parseNotification(notification: Notification): Notification {
    notification.colors = NOTIFICATION_STATUS_TO_COLOR[notification.eventInfo.status.toUpperCase()] || defaultColors;
    notification.state.read = JSON.parse('' + notification.state.read);
    notification.type = `${notification.eventInfo.event} ${notification.eventInfo.status}`;

    const notificationProcessor = NOTIFICATION_PROCESSORS[notification.type];
    if (notificationProcessor) {
      notification.title = this.evaluateProcessor(notificationProcessor.title, notification);
      notification.message = this.evaluateProcessor(notificationProcessor.message, notification);
      notification.link = this.evaluateProcessor(notificationProcessor.link, notification);
    }

    return notification;
  }

  private evaluateProcessor(processor: any, notification: Notification): any {
    return isFunction(processor) ? processor(notification) : processor;
  }

  private subscribeForServerNotifications(): void {
    const notificationsTopicSubscription = this.rxStompService.watch('/user/topic/messages', {ack: 'client-individual'});
    notificationsTopicSubscription.subscribe((body: IMessage) => {
      const notification = JSON.parse(body.body) as Notification;
      // Convert from string to Date
      notification.eventInfo.eventAt = new Date(notification.eventInfo.eventAt);
      this.addNotification(notification);
      this.notificationSubject.next(notification);
    });
  }
}
